import { api } from '@pwa/lib/hono-client';
import { queryOptions } from '@tanstack/react-query';

export class ChatHttpError extends Error {
	readonly status: number;

	constructor(status: number, fallbackMessage: string) {
		super(status === 403 ? 'You do not have permission to view this chat.' : fallbackMessage);
		this.name = 'ChatHttpError';
		this.status = status;
	}
}

export const chatQueries = {
	all: (organizationId: string) => ['chats', organizationId] as const,
	// List chats for org wide
	orgList: (organizationId: string) =>
		queryOptions({
			queryKey: [...chatQueries.all(organizationId), 'org', 'list'],
			queryFn: async () => {
				const res = await api.orgs[':orgId'].chat.chats.$get({
					param: { orgId: organizationId },
				});
				if (!res.ok) throw new ChatHttpError(res.status, 'Failed to fetch org chats');
				const { chats } = await res.json();
				return chats;
			},
		}),

	orgDetail: (chatId: string, organizationId: string) =>
		queryOptions({
			queryKey: [...chatQueries.all(organizationId), 'org', chatId],
			queryFn: async () => {
				const res = await api.orgs[':orgId'].chat.chats[':chatId'].messages.$get({
					param: { orgId: organizationId, chatId },
				});
				if (res.status === 404) return null;
				if (!res.ok) throw new ChatHttpError(res.status, 'Failed to fetch org chat messages');
				const { messages } = await res.json();
				return messages;
			},
		}),

	clientList: (wsId: string, organizationId: string) =>
		queryOptions({
			queryKey: [...chatQueries.all(organizationId), 'workspace', wsId, 'list'],
			queryFn: async () => {
				const res = await api.orgs[':orgId'].chat.ws[':wsId'].chats.$get({
					param: { orgId: organizationId, wsId },
				});
				if (!res.ok) throw new ChatHttpError(res.status, 'Failed to fetch workspace chats');
				const { chats } = await res.json();
				return chats;
			},
		}),

	clientDetail: (wsId: string, chatId: string, organizationId: string) =>
		queryOptions({
			queryKey: [...chatQueries.all(organizationId), 'workspace', wsId, chatId],
			queryFn: async () => {
				const res = await api.orgs[':orgId'].chat.ws[':wsId'].chats[':chatId'].messages.$get({
					param: { orgId: organizationId, wsId, chatId },
				});
				if (res.status === 404) return null;
				if (!res.ok) throw new ChatHttpError(res.status, 'Failed to fetch workspace chat messages');
				const { messages } = await res.json();
				return messages;
			},
		}),
};
